import {
  Checkbox,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Formik, FieldArray } from "formik";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSpace } from "store/slice/space/spaceSlice";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CircularIndeterminate from "components/MDLoading";
import MDSnackbar from "components/MDSnackbar";
import { useMaterialUIController } from "context";
import { getSpaces } from "store/slice/space/spaceSlice";

const CreateVipParking = ({
  dialogOpen,
  onClose,
  isEdit = false,
  data = {},
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [isLoading, setIsLoading] = useState(false);
  const placesData = useSelector((state) => state.places?.selectedPlace);
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  let placeId =
    placesData?._id || new URLSearchParams(location?.search).get("placeId");

  const handleCreateSpace = (spaceData) => {
    setIsLoading(true);
    dispatch(createSpace(spaceData))
      .unwrap()
      .then((res) => {
        setIsLoading(false);
        dispatch(getSpaces(placeId));
        setNotification({
          ...notification,
          color: res?.success ? "success" : "error",
          title: res?.success ? "Success" : "Error",
          content: res?.message,
          icon: res?.success ? "check" : "warning",
          show: true,
        });
        if (res?.success) onClose();
      })
      .catch((err) => {
        setIsLoading(false);
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: err?.message,
          icon: "warning",
          show: true,
        });
      });
  };

  return (
    <>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Creating Space"
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDDialog
        dialogTitle={`${isEdit ? "Edit" : "Create"} Space`}
        open={dialogOpen}
        dialogClose={onClose}
        closeIcon
        maxWidth="sm"
      >
        <DialogContent>
          <Formik
            initialValues={{
              placeId,
              titles: [
                { title: data.title || "", isPrimary: true },
              ],
              addRandomTitle: false,
              isEdit,
              _id: data._id || "",
            }}
            onSubmit={(values) => {
              handleCreateSpace(values);
            }}
          >
            {({ values, setFieldValue, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} mt={2}>
                  {/* Select Number of Titles */}
                  {!isEdit && (
                    <>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel id="select-spaces-label">
                            Select Spaces
                          </InputLabel>
                          <Select
                            labelId="select-spaces-label"
                            label="Select Spaces"
                            value={values.titles.length}
                            onChange={(e) => {
                              const count = Number(e.target.value);
                              const firstTitle = values.titles[0]?.title || "";
                              setFieldValue(
                                "titles",
                                Array.from({ length: count }, (_, index) => ({
                                  title:
                                    index === 0
                                      ? firstTitle
                                      : values.addRandomTitle && firstTitle
                                      ? `${firstTitle} ${index + 1}`
                                      : "",
                                  isPrimary: true,
                                }))
                              );
                            }}
                          >
                            {Array.from({ length: 100 }, (_, i) => i + 1).map(
                              (num) => (
                                <MenuItem key={num} value={num}>
                                  {num}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Checkbox to Add Random Titles */}
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center">
                          <Checkbox
                            checked={values.addRandomTitle}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFieldValue("addRandomTitle", isChecked);

                              if (isChecked && values.titles.length > 0) {
                                const firstTitle =
                                  values.titles[0]?.title || "";
                                setFieldValue(
                                  "titles",
                                  values.titles.map((_, index) => ({
                                    title:
                                      index === 0
                                        ? firstTitle
                                        : firstTitle
                                        ? `${firstTitle} ${index + 1}`
                                        : "",
                                    isPrimary: true,
                                  }))
                                );
                              }
                            }}
                          />
                          <MDTypography variant="body2">
                            Auto-generate Space Numbers
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </>
                  )}

                  <FieldArray name="titles">
                    {() =>
                      values.titles.map((_, index) => (
                        <Grid
                          p={2}
                          container
                          spacing={2}
                          key={index}
                          alignItems="center"
                        >
                          {/* Title Input */}
                          <Grid item xs={8}>
                            <MDInput
                              name={`titles.${index}.title`}
                              value={values.titles[index].title}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                setFieldValue(
                                  `titles.${index}.title`,
                                  newValue
                                );

                                if (index === 0 && values.addRandomTitle) {
                                  setFieldValue(
                                    "titles",
                                    values.titles.map((title, idx) => ({
                                      ...title,
                                      title:
                                        idx === 0
                                          ? newValue
                                          : newValue
                                          ? `${newValue} ${idx + 1}`
                                          : "",
                                    }))
                                  );
                                }
                              }}
                              label={`Space Number ${index + 1}`}
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      ))
                    }
                  </FieldArray>

                  {/* Submit Button */}
                  <Grid item xs={12} style={{ textAlign: "right" }}>
                    <MDButton
                      variant="contained"
                      color={sidenavColor}
                      type="submit"
                    >
                      {isEdit ? "Update" : "Create"}
                    </MDButton>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </MDDialog>
    </>
  );
};

export default CreateVipParking;
